import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useRef, useEffect, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { LuMenu } from "react-icons/lu"; // Menu Icon
import DropdownDark from '../images/download-black.svg'
import DropdownLight from '../images/download-white.svg'
import { FaArrowLeft } from "react-icons/fa"; // Back Arrow Icon
import {
  FaChevronDown,
  FaUser,
  FaSignOutAlt,
  FaTachometerAlt,   // Dashboard
  FaExchangeAlt,     // Transactions
  FaWifi,            // Buy Data
  FaMobileAlt,       // Buy Airtime
  FaWallet,          // Wallet
  FaCog,             // Settings
  FaQuestionCircle
} from "react-icons/fa";
import { Image } from "react-bootstrap";
import Logo from "../images/logoz1.png";
import defaultAvatar from "../images/Avatar.png";
import "../styles/dashboard.css";
import MobileWidget from "./MobileWidget"
import NotificationBell from "../components/NotificationBell";
import DarkModeToggle from "./DarkModeToggle";

const headerTitles = {
  "/dashboard": "Dashboard",
  "/transactions": "Transactions",
  "/buy-data": "Buy Data",
  "/buy-airtime": "Buy Airtime",
  "/wallet": "Wallet",
  "/settings": "Settings",
  "/help": "Help & Support"
};

const headerIcons = {
  "/dashboard": <FaTachometerAlt />,
  "/transactions": <FaExchangeAlt />,
  "/buy-data": <FaWifi />,
  "/buy-airtime": <FaMobileAlt />,
  "/wallet": <FaWallet />,
  "/settings": <FaCog />,
  "/help": <FaQuestionCircle />
};

const DashboardLayout = () => {
  const { currentUser, logout, isDarkMode, setIsDarkMode } = useContext(AuthContext);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [themeLabel, setThemeLabel] = useState("Light Mode");
  const profileMenuRef = useRef(null);
  const sidebarRef = useRef(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [headerTitle, setHeaderTitle] = useState("Dashboard");
  const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false); // Track notification popup state

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login"); // Redirect to login if no token is found
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!currentUser) {
      const handleLogout = async () => {
        await logout();   // clean everything first
        navigate("/login");    // then redirect
      };
      handleLogout();
    }
  }, [currentUser, logout, navigate]);

  useEffect(() => {
    if (location.pathname === "/settings/profile") {
      setHeaderTitle("Profile Settings"); // ✅ Override only for the profile page
    }
    else if (location.pathname === "/result-checker") {
      setHeaderTitle(" Purchase Education Token");
    }

    else if (location.pathname === "/bills-payment") {
      setHeaderTitle(" Pay Electricity Bill");
    }

    else if (location.pathname === "/bulk-sms") {
      setHeaderTitle(" Send Bulk SMS");
    }

    else if (location.pathname === "/recharge-card-printing") {
      setHeaderTitle("Generate Recharge Card Pin");
    }

    else if (location.pathname === "/buy-cabletv") {
      setHeaderTitle(" Cable TV Subscription");
    }

    else if (location.pathname === "/airtime-to-cash") {
      setHeaderTitle(" Airtime to Cash");
    }

    else if (location.pathname === "/withdraw") {
      setHeaderTitle("Withdraw");
    }

    else if (location.pathname === "/virtual-pay") {
      setHeaderTitle("Fund Wallet");
    }

    else if (location.pathname === "/add-funds") {
      setHeaderTitle("Fund Wallet");
    }


    else if (location.pathname.startsWith("/transactions/")) {
      setHeaderTitle("View Transaction ");
    }

    else if (location.pathname === "/electricity-details") {
      setHeaderTitle("Electricity Token Purchases");
    }

    else if (location.pathname === "/settings/security") {
      setHeaderTitle(" Security Settings");
    }

    else if (location.pathname === "/bank-account") {
      setHeaderTitle("Manage Bank Account");
    }

    else if (location.pathname === "/generated-pins") {
      setHeaderTitle("Generated Pins");
    }


    else if (location.pathname === "/result-details") {
      setHeaderTitle("My Education Token Purchases");
    }

    else if (location.pathname === "/forgot-pin") {
      setHeaderTitle("Reset Pin");
    }


    else if (location.pathname === "/help/faq") {
      setHeaderTitle("FAQs");
    }


    else if (location.pathname === "/help/report") {
      setHeaderTitle("Report A Bug");
    }

    else if (location.pathname === "/help/contact") {
      setHeaderTitle("Contact Us");
    }



    else if (location.pathname === "/review") {
      setHeaderTitle("Review");
    }

    else if (location.pathname === "/add-bank-account") {
      setHeaderTitle("Add Bank Account");
    }


    else if (location.pathname === "/settings/general") {
      setHeaderTitle("General Settings");
    }

    else {
      setHeaderTitle(headerTitles[location.pathname] || "");
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileMenu]);


  useEffect(() => {
    if (screenWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleNotificationToggle = (isOpen) => {
    setIsNotificationPopupOpen(isOpen); // Update the popup state
  };

  useEffect(() => {
    setThemeLabel(isDarkMode ? "Dark Mode" : "Light Mode");
  }, [isDarkMode]);


  // Add class to shift content down when popup is open
  const contentClass = isNotificationPopupOpen ? "shifted-down" : "";

  // ✅ Override Back Navigation for Settings on Mobile
  const handleBackNavigation = () => {
    if (screenWidth <= 1024) {
      if (location.pathname.startsWith("/settings/")) {
        navigate("/settings", { replace: true }); // ✅ Go back to settings page if inside settings sub-page
      } else if (location.pathname === "/settings") {
        navigate("/dashboard", { replace: true }); // ✅ Go back to dashboard if inside settings main page
      } else if (location.pathname.startsWith("/help/")) {
        navigate("/help", { replace: true }); // ✅ Go back to settings page if inside settings sub-page
      } else if (location.pathname === "/help") {
        navigate("/dashboard", { replace: true }); // ✅ Go back to dashboard if inside settings main page
      } else {
        navigate(-1); // Default behavior for other pages
      }
    } else {
      navigate(-1);
    }
  };
  const showBackButton = screenWidth <= 1022 && location.pathname !== "/dashboard" && location.pathname !== "/receipt";

  return (
    <div className="dashboard-wrapper">
      {isSidebarOpen && <div className="overlay" onClick={() => setIsSidebarOpen(false)}></div>}

      {/* Sidebar */}
      <aside ref={sidebarRef} className={`sidebar ${isSidebarOpen ? "open" : ""} ${screenWidth <= 768 ? "hidden" : ""}`}>
        <div className="logo-container">
          {screenWidth > 1022 ? (
            <img src={Logo} alt="Logo" className="dash-logo" />
          ) : (
            <div className="sidebar-user-info">
              <Image
                src={currentUser?.profileImage || defaultAvatar}
                roundedCircle
                width={40}
                height={40}
                alt="Profile Avatar"
                className="dash-profile-avatar"
              />
              <div className="sidebar-user-details">
                <div className="username">{currentUser?.username}</div>
                <div className="email">{currentUser?.email}</div>
              </div>
            </div>

          )}
        </div>

        {/* Navigation Links */}
        <nav className="nav-links">
          {Object.keys(headerTitles).map((path) => (
            <Link
              key={path}
              to={path}
              className={location.pathname === path ? "active-link" : ""}
              onClick={() => screenWidth <= 1022 && setIsSidebarOpen(false)}
            >
              <span className="nav-icon">{headerIcons[path]}</span>
              <span className="nav-label">{headerTitles[path]}</span>
            </Link>
          ))}

          {/* 🚀 Logout link only for small screens */}
          {screenWidth <= 1022 && (
            <a
              className="sidebar-logout-btn"
              onClick={logout}
            >
              <FaSignOutAlt />
              Logout
            </a>
          )}
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`content ${contentClass}`}>
        {/* Header */}
        <header className="dashboard-header">
          {/* Left Side */}
          <div className="header-left">
            {screenWidth <= 1022 && location.pathname === "/receipt" ? null : (
              showBackButton ? (
                <FaArrowLeft className="back-btn" onClick={handleBackNavigation} />
              ) : (
                !isSidebarOpen && <LuMenu className="menu-icon" onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
              )
            )}

            {/* Show title on desktop for non-dashboard pages */}
            {screenWidth > 1022 && location.pathname !== "/dashboard" && (
              <h2 className="header-title">{headerTitle}</h2>
            )}

          </div>



          {/* Center Title */}
          <div className={`header-center ${screenWidth <= 1022 ? "mobile-center" : ""}`}>
            {screenWidth <= 1022 ? (
              <h2 className="header-title">{headerTitle}</h2>
            ) : (
              location.pathname === "/dashboard" && (
                <section className="dash-user-greeting">
                  <h2>Hello <span className="dash-small-size">{currentUser?.username || "User"}, 👋 </span></h2>
                </section>
              )
            )}
          </div>

          {/* Right Side */}

          <div className="header-right">
            {(screenWidth > 1022 || location.pathname === "/dashboard") && (
              <div className="profile-notification-wrapper">
                <DarkModeToggle onToggle={() => setIsDarkMode(prev => !prev)} />

                {screenWidth > 1022 && (
                  <div className="theme-toggle-label-wrapper">

                    <span className="theme-label">{themeLabel}</span>
                  </div>
                )}

                <NotificationBell onToggle={handleNotificationToggle} />

                {/* Only show profile image on large screens */}
                {screenWidth > 1022 && (
                  <div className="dash-profile-dropdown-wrapper" ref={profileMenuRef} onClick={() => setShowProfileMenu(!showProfileMenu)}>
                    <Image
                      src={currentUser?.profileImage || defaultAvatar}
                      roundedCircle
                      width={40}
                      height={40}
                      alt="Profile Avatar"
                      className="dash-profile-avatar"
                    />
                    <img
                      src={isDarkMode ? DropdownLight : DropdownDark}
                      alt="Dropdown"
                      className="custom-dropdown-icon"
                    />

                    {showProfileMenu && ( 
                      <div className="dash-profile-dropdown">
                        <div className="dropdown-grid">
                          <div className="dropdown-item d-item" onClick={() => navigate("/settings/profile")}>
                            <FaUser /> <span>Profile</span>
                          </div>
                          <div className="dropdown-item d-item" onClick={logout}>
                            <FaSignOutAlt /> <span>Logout</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                )}


              </div>
            )}
          </div>
        </header>


        {/* ✅ Pass setHeaderTitle to Outlet for dynamic updates */}
        <Outlet context={{ setHeaderTitle }} />
      </main>
      {screenWidth <= 768 && <MobileWidget />}
    </div>
  );
};

export default DashboardLayout;
